/* eslint-disable react/no-unknown-property */
import { useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import { Float, Sparkles, Html } from '@react-three/drei';
import * as THREE from 'three';
import BeautyConsultant, { CONSULTANT_STATES } from './BeautyConsultant';
import Laptop from './Laptop';
import HolographicSpellCheckPanel from './HolographicSpellCheckPanel';

// Vanity Table Component with improved visibility
const VanityTable = ({ position = [0, 0, 0] }) => {
  return (
    <group position={position}>
      {/* Table top - white marble with subtle gray veining effect */}
      <mesh position={[0, 0.75, 0]} castShadow receiveShadow>
        <boxGeometry args={[2.5, 0.08, 0.8]} />
        <meshStandardMaterial color="#FFFFFF" roughness={0.2} metalness={0.05} />
      </mesh>
      
      {/* Gold legs - more saturated gold */}
      <mesh position={[-1.1, 0.375, 0.3]} castShadow>
        <cylinderGeometry args={[0.03, 0.03, 0.75]} />
        <meshStandardMaterial color="#D4A574" roughness={0.25} metalness={0.7} />
      </mesh>
      <mesh position={[1.1, 0.375, 0.3]} castShadow>
        <cylinderGeometry args={[0.03, 0.03, 0.75]} />
        <meshStandardMaterial color="#D4A574" roughness={0.25} metalness={0.7} />
      </mesh>
      <mesh position={[-1.1, 0.375, -0.3]} castShadow>
        <cylinderGeometry args={[0.03, 0.03, 0.75]} />
        <meshStandardMaterial color="#D4A574" roughness={0.25} metalness={0.7} />
      </mesh>
      <mesh position={[1.1, 0.375, -0.3]} castShadow>
        <cylinderGeometry args={[0.03, 0.03, 0.75]} />
        <meshStandardMaterial color="#D4A574" roughness={0.25} metalness={0.7} />
      </mesh>
      
      {/* Drawer - soft pink/rose for brand color visibility */}
      <mesh position={[0, 0.55, 0.05]} castShadow>
        <boxGeometry args={[1.2, 0.3, 0.6]} />
        <meshStandardMaterial color="#F5E6E6" roughness={0.4} />
      </mesh>
      
      {/* Drawer handle - gold accent */}
      <mesh position={[0, 0.55, 0.36]}>
        <boxGeometry args={[0.3, 0.02, 0.02]} />
        <meshStandardMaterial color="#D4A574" roughness={0.25} metalness={0.7} />
      </mesh>
    </group>
  );
};

// LED Mirror Component - Premium Hollywood Vanity Mirror
const LEDMirror = ({ position = [0, 0, 0], isActive, isThinking, onInteract }) => {
  const ledRef = useRef();
  const outerGlowRef = useRef();
  const innerGlowRef = useRef();
  const [hovered, setHovered] = useState(false);
  
  useFrame((state) => {
    const time = state.clock.elapsedTime;
    
    // LED ring animation - soft pulsing
    if (ledRef.current && ledRef.current.material) {
      const baseIntensity = isThinking ? 2.5 : isActive ? 1.5 : 0.8;
      const pulseSpeed = isThinking ? 4 : 2;
      ledRef.current.material.emissiveIntensity = baseIntensity + Math.sin(time * pulseSpeed) * 0.3;
    }
    
    // Outer glow pulse animation
    if (outerGlowRef.current) {
      const glowBase = isThinking ? 0.5 : isActive ? 0.3 : 0.15;
      const glowPulse = isThinking ? 0.15 : 0.08;
      outerGlowRef.current.material.opacity = glowBase + Math.sin(time * 2.5) * glowPulse;
      // Subtle scale pulse when AI is thinking
      if (isThinking) {
        const scale = 1 + Math.sin(time * 3) * 0.02;
        outerGlowRef.current.scale.set(scale, scale, 1);
      }
    }
    
    // Inner reflection shimmer
    if (innerGlowRef.current) {
      innerGlowRef.current.material.opacity = 0.1 + Math.sin(time * 1.5 + 1) * 0.05;
    }
  });
  
  return (
    <group position={position}>
      {/* Outer soft glow halo */}
      <mesh ref={outerGlowRef} position={[0, 0, -0.02]}>
        <planeGeometry args={[1.8, 2.2]} />
        <meshBasicMaterial 
          color={isThinking ? "#FFB6C1" : "#FFD4E5"} 
          transparent 
          opacity={0.2}
          side={THREE.DoubleSide}
        />
      </mesh>
      
      {/* Gold frame with premium finish */}
      <mesh castShadow>
        <boxGeometry args={[1.4, 1.8, 0.1]} />
        <meshStandardMaterial 
          color="#D4A574" 
          roughness={0.15} 
          metalness={0.7} 
          emissive="#FFE4C4"
          emissiveIntensity={isActive ? 0.15 : 0.08}
        />
      </mesh>
      
      {/* Mirror glass - visible reflective surface */}
      <mesh position={[0, 0, 0.06]}>
        <planeGeometry args={[1.2, 1.6]} />
        <meshStandardMaterial 
          color="#E8E8E8" 
          roughness={0.05} 
          metalness={0.9}
        />
      </mesh>
      
      {/* Inner shimmer effect */}
      <mesh ref={innerGlowRef} position={[0, 0, 0.062]}>
        <planeGeometry args={[1.15, 1.55]} />
        <meshBasicMaterial 
          color="#FFE4E1" 
          transparent 
          opacity={0.1}
          side={THREE.DoubleSide}
        />
      </mesh>
      
      {/* LED ring - main glow source */}
      <mesh ref={ledRef} position={[0, 0, 0.065]}>
        <ringGeometry args={[0.55, 0.65, 64]} />
        <meshStandardMaterial 
          color="#FFF0F5" 
          emissive={isThinking ? "#FF69B4" : "#FFB6C1"}
          emissiveIntensity={1.0}
          transparent
          opacity={0.95}
          side={THREE.DoubleSide}
        />
      </mesh>
      
      {/* Hollywood bulb dots around frame */}
      {[...Array(16)].map((_, i) => {
        const angle = (i / 16) * Math.PI * 2;
        const radius = 0.72;
        const x = Math.cos(angle) * radius * 0.75;
        const y = Math.sin(angle) * radius;
        return (
          <mesh key={`bulb-${i}`} position={[x, y, 0.07]}>
            <sphereGeometry args={[0.025, 12, 12]} />
            <meshStandardMaterial 
              color="#FFFAF0" 
              emissive="#FFE4E1"
              emissiveIntensity={isActive ? 1.2 : 0.6}
            />
          </mesh>
        );
      })}
      
      {/* Point light from mirror - premium glow */}
      <pointLight 
        position={[0, 0, 0.3]} 
        intensity={isThinking ? 1.5 : isActive ? 1.0 : 0.5} 
        color={isThinking ? "#FFB6C1" : "#FFE4E1"} 
        distance={4}
        decay={2}
      />
      
      {/* Secondary ambient glow */}
      <pointLight 
        position={[0, 0, 0.5]} 
        intensity={isActive ? 0.4 : 0.2} 
        color="#FFD4E5" 
        distance={2}
        decay={2}
      />
      
      {/* Interaction hint */}
      {hovered && !isActive && (
        <Html position={[0, -1.1, 0.1]} center distanceFactor={10}>
          <div 
            className="animate-fade-in"
            style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(253,248,246,0.95) 100%)',
              padding: '8px 16px',
              borderRadius: '12px',
              fontSize: '14px',
              color: '#BA7B7C',
              whiteSpace: 'nowrap',
              boxShadow: '0 8px 24px rgba(186,123,124,0.25), 0 2px 8px rgba(0,0,0,0.1)',
              border: '1px solid rgba(186,123,124,0.2)',
              fontFamily: "'Cormorant Garamond', serif",
              fontWeight: '500'
            }}
          >
            ✨ Presiona E para interactuar
          </div>
        </Html>
      )}
      
      {/* Invisible interaction mesh */}
      <mesh 
        position={[0, 0, 0.1]}
        onPointerOver={() => setHovered(true)}
        onPointerOut={() => setHovered(false)}
        onClick={onInteract}
      >
        <planeGeometry args={[1.4, 1.8]} />
        <meshBasicMaterial transparent opacity={0} />
      </mesh>
    </group>
  );
};

// Perfume Bottle
const PerfumeBottle = ({ position, color = "#FFB6C1", scale = 1, highlighted = false }) => {
  const bottleRef = useRef();
  
  useFrame(() => {
    if (highlighted && bottleRef.current) {
      bottleRef.current.rotation.y += 0.01;
    }
  });
  
  return (
    <Float speed={0.5} rotationIntensity={0.1} floatIntensity={0.1}>
      <group position={position} scale={scale} ref={bottleRef}>
        <mesh castShadow>
          <cylinderGeometry args={[0.04, 0.05, 0.15, 16]} />
          <meshStandardMaterial 
            color={color}
            roughness={0.2}
            metalness={0.1}
            transparent
            opacity={0.8}
            emissive={highlighted ? color : "#000000"}
            emissiveIntensity={highlighted ? 0.3 : 0}
          />
        </mesh>
        
        <mesh position={[0, 0.1, 0]} castShadow>
          <cylinderGeometry args={[0.015, 0.025, 0.05, 16]} />
          <meshStandardMaterial color={color} roughness={0.2} transparent opacity={0.8} />
        </mesh>
        
        <mesh position={[0, 0.145, 0]}>
          <cylinderGeometry args={[0.025, 0.025, 0.04, 16]} />
          <meshStandardMaterial color="#D4A574" roughness={0.3} metalness={0.5} />
        </mesh>
      </group>
    </Float>
  );
};

// Lipstick
const Lipstick = ({ position, color = "#BA7B7C", scale = 1, highlighted = false }) => {
  const lipstickRef = useRef();
  
  useFrame(() => {
    if (highlighted && lipstickRef.current) {
      lipstickRef.current.rotation.y += 0.015;
    }
  });
  
  return (
    <group position={position} scale={scale} ref={lipstickRef}>
      <mesh castShadow>
        <cylinderGeometry args={[0.015, 0.015, 0.08, 16]} />
        <meshStandardMaterial 
          color="#1C1917" 
          roughness={0.2} 
          metalness={0.8}
          emissive={highlighted ? "#FFD700" : "#000000"}
          emissiveIntensity={highlighted ? 0.2 : 0}
        />
      </mesh>
      
      <mesh position={[0, 0.055, 0]} rotation={[0, 0, Math.PI]}>
        <coneGeometry args={[0.012, 0.03, 16]} />
        <meshStandardMaterial color={color} roughness={0.3} />
      </mesh>
      
      <mesh position={[0, 0.03, 0]}>
        <torusGeometry args={[0.016, 0.003, 16, 32]} />
        <meshStandardMaterial color="#D4A574" roughness={0.3} metalness={0.7} />
      </mesh>
    </group>
  );
};

// Decorative Shelf - improved visibility
const DecorativeShelf = ({ position }) => {
  return (
    <group position={position}>
      <mesh castShadow receiveShadow>
        <boxGeometry args={[1.2, 0.03, 0.25]} />
        <meshStandardMaterial color="#FFFFFF" roughness={0.25} metalness={0.05} />
      </mesh>
      
      <mesh position={[-0.55, -0.08, 0.1]}>
        <boxGeometry args={[0.03, 0.15, 0.03]} />
        <meshStandardMaterial color="#D4A574" roughness={0.3} metalness={0.6} />
      </mesh>
      
      <mesh position={[0.55, -0.08, 0.1]}>
        <boxGeometry args={[0.03, 0.15, 0.03]} />
        <meshStandardMaterial color="#D4A574" roughness={0.3} metalness={0.6} />
      </mesh>
    </group>
  );
};

// Room Floor - Luxury herringbone wood pattern effect
const StudioFloor = () => {
  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]} receiveShadow>
      <planeGeometry args={[12, 12]} />
      <meshStandardMaterial color="#C4B8A8" roughness={0.4} metalness={0.02} />
    </mesh>
  );
};

// Room Walls - Elegant paneled walls
const StudioWalls = () => {
  return (
    <group>
      {/* Back wall - rose-tinted cream with panels */}
      <mesh position={[0, 2.5, -3.5]} receiveShadow>
        <planeGeometry args={[12, 5]} />
        <meshStandardMaterial color="#E8D8D0" roughness={0.8} />
      </mesh>
      
      {/* Left wall */}
      <mesh position={[-5.5, 2.5, 0]} rotation={[0, Math.PI / 2, 0]} receiveShadow>
        <planeGeometry args={[8, 5]} />
        <meshStandardMaterial color="#EDE0DA" roughness={0.8} />
      </mesh>
      
      {/* Right wall */}
      <mesh position={[5.5, 2.5, 0]} rotation={[0, -Math.PI / 2, 0]} receiveShadow>
        <planeGeometry args={[8, 5]} />
        <meshStandardMaterial color="#EDE0DA" roughness={0.8} />
      </mesh>
      
      {/* Front wall (behind camera) */}
      <mesh position={[0, 2.5, 5]} rotation={[0, Math.PI, 0]} receiveShadow>
        <planeGeometry args={[12, 5]} />
        <meshStandardMaterial color="#EDE0DA" roughness={0.8} />
      </mesh>
      
      {/* Wall trim/molding - bottom */}
      <mesh position={[0, 0.1, -3.45]}>
        <boxGeometry args={[12, 0.2, 0.1]} />
        <meshStandardMaterial color="#FFFFFF" roughness={0.3} />
      </mesh>
      <mesh position={[-5.45, 0.1, 0]} rotation={[0, Math.PI / 2, 0]}>
        <boxGeometry args={[8, 0.2, 0.1]} />
        <meshStandardMaterial color="#FFFFFF" roughness={0.3} />
      </mesh>
      <mesh position={[5.45, 0.1, 0]} rotation={[0, Math.PI / 2, 0]}>
        <boxGeometry args={[8, 0.2, 0.1]} />
        <meshStandardMaterial color="#FFFFFF" roughness={0.3} />
      </mesh>
    </group>
  );
};

// Ceiling with decorative elements
const StudioCeiling = () => {
  return (
    <group>
      {/* Main ceiling */}
      <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, 5, 0]}>
        <planeGeometry args={[12, 12]} />
        <meshStandardMaterial color="#FDFBF9" roughness={0.9} />
      </mesh>
      
      {/* Ceiling crown molding */}
      <mesh position={[0, 4.9, -3.45]}>
        <boxGeometry args={[12, 0.15, 0.15]} />
        <meshStandardMaterial color="#FFFFFF" roughness={0.3} />
      </mesh>
      <mesh position={[-5.45, 4.9, 0]} rotation={[0, Math.PI / 2, 0]}>
        <boxGeometry args={[8, 0.15, 0.15]} />
        <meshStandardMaterial color="#FFFFFF" roughness={0.3} />
      </mesh>
      <mesh position={[5.45, 4.9, 0]} rotation={[0, Math.PI / 2, 0]}>
        <boxGeometry args={[8, 0.15, 0.15]} />
        <meshStandardMaterial color="#FFFFFF" roughness={0.3} />
      </mesh>
    </group>
  );
};

// Decorative chandelier
const Chandelier = ({ position = [0, 4, -1] }) => {
  const chandelierRef = useRef();
  
  useFrame((state) => {
    if (chandelierRef.current) {
      chandelierRef.current.rotation.y = Math.sin(state.clock.elapsedTime * 0.1) * 0.02;
    }
  });
  
  return (
    <group ref={chandelierRef} position={position}>
      {/* Central rod */}
      <mesh>
        <cylinderGeometry args={[0.02, 0.02, 0.5, 8]} />
        <meshStandardMaterial color="#D4A574" roughness={0.3} metalness={0.7} />
      </mesh>
      
      {/* Crystal ring */}
      <mesh position={[0, -0.25, 0]}>
        <torusGeometry args={[0.3, 0.02, 8, 24]} />
        <meshStandardMaterial color="#D4A574" roughness={0.3} metalness={0.7} />
      </mesh>
      
      {/* Hanging crystals */}
      {[...Array(8)].map((_, i) => {
        const angle = (i / 8) * Math.PI * 2;
        return (
          <group key={`crystal-${i}`} position={[Math.cos(angle) * 0.3, -0.35, Math.sin(angle) * 0.3]}>
            <mesh>
              <cylinderGeometry args={[0.005, 0.005, 0.15, 4]} />
              <meshStandardMaterial color="#D4A574" roughness={0.3} metalness={0.6} />
            </mesh>
            <mesh position={[0, -0.1, 0]}>
              <octahedronGeometry args={[0.03]} />
              <meshStandardMaterial 
                color="#FFFFFF" 
                roughness={0.1} 
                metalness={0.1}
                transparent
                opacity={0.8}
              />
            </mesh>
          </group>
        );
      })}
      
      {/* Light source */}
      <pointLight position={[0, -0.2, 0]} intensity={0.5} color="#FFF5E6" distance={5} />
    </group>
  );
};

// Indoor plant
const IndoorPlant = ({ position = [0, 0, 0], scale = 1 }) => {
  return (
    <group position={position} scale={scale}>
      {/* Pot */}
      <mesh position={[0, 0.15, 0]} castShadow>
        <cylinderGeometry args={[0.12, 0.1, 0.3, 16]} />
        <meshStandardMaterial color="#D4A574" roughness={0.5} />
      </mesh>
      
      {/* Soil */}
      <mesh position={[0, 0.28, 0]}>
        <cylinderGeometry args={[0.1, 0.1, 0.05, 16]} />
        <meshStandardMaterial color="#5D4037" roughness={0.9} />
      </mesh>
      
      {/* Leaves */}
      {[...Array(5)].map((_, i) => {
        const angle = (i / 5) * Math.PI * 2;
        const height = 0.35 + Math.random() * 0.2;
        return (
          <mesh 
            key={`leaf-${i}`} 
            position={[Math.cos(angle) * 0.05, height, Math.sin(angle) * 0.05]}
            rotation={[0.3 + Math.random() * 0.2, angle, 0]}
          >
            <planeGeometry args={[0.08, 0.2]} />
            <meshStandardMaterial color="#4A7C59" roughness={0.6} side={THREE.DoubleSide} />
          </mesh>
        );
      })}
    </group>
  );
};

// Wall art/decorative mirror
const WallDecor = ({ position = [0, 0, 0], rotation = [0, 0, 0] }) => {
  return (
    <group position={position} rotation={rotation}>
      {/* Frame */}
      <mesh>
        <boxGeometry args={[0.6, 0.8, 0.03]} />
        <meshStandardMaterial color="#D4A574" roughness={0.3} metalness={0.6} />
      </mesh>
      
      {/* Mirror surface */}
      <mesh position={[0, 0, 0.02]}>
        <planeGeometry args={[0.5, 0.7]} />
        <meshStandardMaterial color="#E8E8E8" roughness={0.05} metalness={0.9} />
      </mesh>
    </group>
  );
};

// Luxury Velvet Stool
const VanityStool = ({ position = [0, 0, 0] }) => {
  return (
    <group position={position}>
      {/* Velvet seat cushion - soft pink */}
      <mesh position={[0, 0.45, 0]} castShadow>
        <cylinderGeometry args={[0.25, 0.25, 0.1, 24]} />
        <meshStandardMaterial color="#E8B4B8" roughness={0.8} metalness={0} />
      </mesh>
      
      {/* Gold rim */}
      <mesh position={[0, 0.4, 0]}>
        <torusGeometry args={[0.25, 0.015, 8, 24]} />
        <meshStandardMaterial color="#D4A574" roughness={0.3} metalness={0.7} />
      </mesh>
      
      {/* Gold legs */}
      {[0, Math.PI * 0.66, Math.PI * 1.33].map((angle, i) => (
        <mesh 
          key={`stool-leg-${i}`}
          position={[Math.sin(angle) * 0.18, 0.2, Math.cos(angle) * 0.18]} 
          castShadow
        >
          <cylinderGeometry args={[0.02, 0.025, 0.4, 8]} />
          <meshStandardMaterial color="#D4A574" roughness={0.3} metalness={0.7} />
        </mesh>
      ))}
      
      {/* Cross support */}
      <mesh position={[0, 0.15, 0]}>
        <torusGeometry args={[0.12, 0.01, 6, 24]} />
        <meshStandardMaterial color="#D4A574" roughness={0.3} metalness={0.6} />
      </mesh>
    </group>
  );
};

// Makeup Brush Holder
const BrushHolder = ({ position = [0, 0, 0] }) => {
  return (
    <group position={position}>
      {/* Glass/crystal holder */}
      <mesh castShadow>
        <cylinderGeometry args={[0.06, 0.05, 0.12, 12]} />
        <meshStandardMaterial 
          color="#FFFFFF" 
          roughness={0.1} 
          metalness={0.1}
          transparent
          opacity={0.7}
        />
      </mesh>
      
      {/* Gold rim */}
      <mesh position={[0, 0.06, 0]}>
        <torusGeometry args={[0.06, 0.005, 8, 12]} />
        <meshStandardMaterial color="#D4A574" roughness={0.3} metalness={0.7} />
      </mesh>
      
      {/* Brushes */}
      {[...Array(5)].map((_, i) => {
        const angle = (i / 5) * Math.PI * 2;
        const colors = ['#1C1917', '#8B4513', '#D4A574', '#1C1917', '#BA7B7C'];
        return (
          <group key={`brush-${i}`}>
            {/* Handle */}
            <mesh position={[Math.sin(angle) * 0.03, 0.12, Math.cos(angle) * 0.03]}>
              <cylinderGeometry args={[0.008, 0.008, 0.15, 6]} />
              <meshStandardMaterial color={colors[i]} roughness={0.4} />
            </mesh>
            {/* Bristles */}
            <mesh position={[Math.sin(angle) * 0.03, 0.22, Math.cos(angle) * 0.03]}>
              <coneGeometry args={[0.015, 0.04, 8]} />
              <meshStandardMaterial color="#F5E6E6" roughness={0.9} />
            </mesh>
          </group>
        );
      })}
    </group>
  );
};

// Compact Mirror (decorative)
const CompactMirror = ({ position = [0, 0, 0], rotation = [0, 0, 0] }) => {
  return (
    <group position={position} rotation={rotation}>
      {/* Case */}
      <mesh castShadow>
        <cylinderGeometry args={[0.05, 0.05, 0.015, 16]} />
        <meshStandardMaterial color="#FFB6C1" roughness={0.3} metalness={0.4} />
      </mesh>
      {/* Gold edge */}
      <mesh position={[0, 0.007, 0]}>
        <torusGeometry args={[0.05, 0.003, 8, 16]} />
        <meshStandardMaterial color="#D4A574" roughness={0.3} metalness={0.7} />
      </mesh>
    </group>
  );
};

// Tissue Box (luxury)
const TissueBox = ({ position = [0, 0, 0] }) => {
  return (
    <group position={position}>
      {/* Box */}
      <mesh castShadow>
        <boxGeometry args={[0.15, 0.08, 0.1]} />
        <meshStandardMaterial color="#FFFFFF" roughness={0.3} />
      </mesh>
      {/* Gold trim */}
      <mesh position={[0, 0.041, 0]}>
        <boxGeometry args={[0.14, 0.002, 0.09]} />
        <meshStandardMaterial color="#D4A574" roughness={0.3} metalness={0.6} />
      </mesh>
      {/* Opening */}
      <mesh position={[0, 0.042, 0]}>
        <boxGeometry args={[0.08, 0.002, 0.03]} />
        <meshStandardMaterial color="#F5F0EB" roughness={0.5} />
      </mesh>
    </group>
  );
};

// Ring/Jewelry Dish
const JewelryDish = ({ position = [0, 0, 0] }) => {
  return (
    <group position={position}>
      {/* Dish */}
      <mesh castShadow receiveShadow>
        <cylinderGeometry args={[0.06, 0.05, 0.02, 16]} />
        <meshStandardMaterial color="#FFFFFF" roughness={0.2} metalness={0.1} />
      </mesh>
      {/* Gold rim */}
      <mesh position={[0, 0.01, 0]}>
        <torusGeometry args={[0.06, 0.003, 8, 16]} />
        <meshStandardMaterial color="#D4A574" roughness={0.3} metalness={0.7} />
      </mesh>
      {/* Decorative pearl */}
      <mesh position={[0.02, 0.02, 0]}>
        <sphereGeometry args={[0.015, 12, 12]} />
        <meshStandardMaterial color="#FFF8F0" roughness={0.2} metalness={0.3} />
      </mesh>
    </group>
  );
};

// Studio Lighting - Enhanced for better visibility
const StudioLighting = ({ isActive }) => {
  return (
    <>
      {/* Main ambient - increased intensity for visibility */}
      <ambientLight intensity={1.2} color="#FFFFFF" />
      
      {/* Key light - bright warm white from above-front */}
      <directionalLight
        position={[2, 6, 4]}
        intensity={isActive ? 2.0 : 1.8}
        color="#FFFAF0"
        castShadow
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
        shadow-camera-far={20}
        shadow-camera-left={-5}
        shadow-camera-right={5}
        shadow-camera-top={5}
        shadow-camera-bottom={-5}
      />
      
      {/* Fill light from front - increased */}
      <directionalLight
        position={[0, 3, 5]}
        intensity={0.8}
        color="#FFFFFF"
      />
      
      {/* Pink accent light - left side */}
      <pointLight 
        position={[-3, 2.5, 0]} 
        intensity={isActive ? 1.2 : 0.8} 
        color="#FFB6C1" 
        distance={8}
        decay={2}
      />
      
      {/* Pink accent light - right side */}
      <pointLight 
        position={[3, 2.5, 0]} 
        intensity={isActive ? 1.2 : 0.8} 
        color="#FFD4E5" 
        distance={8}
        decay={2}
      />
      
      {/* Gold/warm accent light - vanity area */}
      <pointLight 
        position={[0, 2, -1.5]} 
        intensity={isActive ? 1.0 : 0.7} 
        color="#FFE4C4" 
        distance={6}
        decay={2}
      />
      
      {/* Front fill light to illuminate the vanity area */}
      <pointLight position={[0, 2, 0]} intensity={0.8} color="#FFFFFF" />
      
      {/* Back fill light */}
      <pointLight position={[0, 3, -3.5]} intensity={0.5} color="#FFE4E1" />
      
      {/* Spotlight on mirror - hero light */}
      <spotLight 
        position={[0, 4, -1]} 
        angle={0.4} 
        penumbra={0.6} 
        intensity={isActive ? 2.0 : 1.5} 
        color="#FFF0F5"
        castShadow
      />
      
      {/* Hemisphere light for natural fill - increased */}
      <hemisphereLight 
        skyColor="#FFFFFF" 
        groundColor="#D8CFC5" 
        intensity={0.5} 
      />
    </>
  );
};

// Main Beauty Studio Scene
const BeautyStudioScene = ({ 
  isMirrorActive = false, 
  isAIThinking = false, 
  onMirrorInteract,
  highlightedProducts = [],
  consultantState = CONSULTANT_STATES.IDLE,
  showWelcome = false,
  spellCheckData = null,
  onSpellCheckConfirm,
  onSpellCheckEdit,
  products = []
}) => {
  return (
    <>
      {/* Warm cream/blush background - luxury boutique feel */}
      <color attach="background" args={['#FDF8F6']} />
      
      <StudioLighting isActive={isMirrorActive} />
      <StudioFloor />
      <StudioWalls />
      <StudioCeiling />
      
      {/* Decorative chandelier */}
      <Chandelier position={[0, 4.5, 0]} />
      
      {/* Indoor plants for ambiance */}
      <IndoorPlant position={[-3.5, 0, -2.5]} scale={1.2} />
      <IndoorPlant position={[3.5, 0, -2.5]} scale={1} />
      <IndoorPlant position={[-4, 0, 2]} scale={0.8} />
      <IndoorPlant position={[4.2, 0, 1]} scale={1.1} />
      
      {/* Wall decor */}
      <WallDecor position={[-3.5, 2, -3.4]} />
      <WallDecor position={[3.5, 2, -3.4]} />
      
      {/* Main furniture */}
      <VanityTable position={[0, 0, -2]} />
      <VanityStool position={[0, 0, -1.2]} />
      
      <LEDMirror 
        position={[0, 1.7, -2.9]} 
        isActive={isMirrorActive}
        isThinking={isAIThinking}
        onInteract={onMirrorInteract}
      />
      
      {/* Phase 3: Laptop on vanity */}
      <Laptop 
        position={[0.5, 0.79, -1.85]} 
        isTyping={consultantState === CONSULTANT_STATES.TYPING}
        screenGlow={isAIThinking}
      />
      
      {/* Phase 3: Beauty Consultant */}
      <BeautyConsultant 
        state={consultantState}
        showWelcome={showWelcome}
      />
      
      {/* Phase 3: Holographic Spell Check Panel */}
      <HolographicSpellCheckPanel
        isVisible={spellCheckData !== null}
        original={spellCheckData?.original || ''}
        corrected={spellCheckData?.corrected || ''}
        correctionsSummary={spellCheckData?.correctionsSummary || ''}
        onConfirm={onSpellCheckConfirm}
        onEdit={onSpellCheckEdit}
        position={[0, 1.9, -2.3]}
      />
      
      {/* Shelves with products */}
      <DecorativeShelf position={[-2, 1.5, -3.35]} />
      <DecorativeShelf position={[2, 1.5, -3.35]} />
      
      {/* Perfume bottles on shelves */}
      <PerfumeBottle position={[-2.3, 1.6, -3.3]} color="#FFB6C1" highlighted={highlightedProducts.includes('perfume1')} />
      <PerfumeBottle position={[-2, 1.6, -3.3]} color="#E6E6FA" scale={0.9} />
      <PerfumeBottle position={[-1.7, 1.6, -3.3]} color="#FFD700" scale={0.85} />
      <PerfumeBottle position={[1.7, 1.6, -3.3]} color="#87CEEB" />
      <PerfumeBottle position={[2, 1.6, -3.3]} color="#DDA0DD" scale={0.95} />
      <PerfumeBottle position={[2.3, 1.6, -3.3]} color="#F0E68C" scale={0.9} />
      
      {/* Lipsticks on vanity */}
      <Lipstick position={[-0.8, 0.83, -1.8]} color="#BA7B7C" highlighted={highlightedProducts.includes('lipstick1')} />
      <Lipstick position={[-0.7, 0.83, -1.8]} color="#FF6B6B" />
      <Lipstick position={[-0.6, 0.83, -1.8]} color="#E91E63" />
      <Lipstick position={[-0.5, 0.83, -1.8]} color="#C71585" />
      
      {/* Vanity accessories */}
      <BrushHolder position={[-0.9, 0.83, -1.9]} />
      <CompactMirror position={[0.8, 0.83, -1.85]} rotation={[-0.1, 0.3, 0]} />
      <TissueBox position={[1.0, 0.83, -1.75]} />
      <JewelryDish position={[-0.3, 0.83, -1.75]} />
      
      {/* Floating sparkles - magical ambiance */}
      <Sparkles
        count={isMirrorActive ? 150 : 80}
        scale={[8, 4, 8]}
        size={isAIThinking ? 2 : 1}
        speed={isAIThinking ? 0.8 : 0.3}
        color="#FFD4E5"
      />
      
      {/* Additional gold sparkles for luxury feel */}
      <Sparkles
        count={40}
        scale={[6, 3, 6]}
        size={0.8}
        speed={0.2}
        color="#D4A574"
        opacity={0.5}
      />
    </>
  );
};

export default BeautyStudioScene;
export { CONSULTANT_STATES };
